import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems, 
  itemsPerPage, 
  onPageChange, 
  onItemsPerPageChange,
  showItemsPerPage = true,
  itemLabel = 'linhas' // Permite customizar o label (ex: 'contas', 'linhas', etc)
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[color:var(--surface)] text-[color:var(--text-primary)] p-4 rounded-lg border border-[color:var(--border)] shadow-sm">
      {/* Informações dos itens */}
      <div className="text-sm text-[color:var(--text-muted)]">
        {startItem} - {endItem} de {totalItems.toLocaleString('pt-BR')} {itemLabel}
      </div>

      {/* Controles de itens por página */}
      {showItemsPerPage && (
        <div className="flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
          <span>Itens por página:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(parseInt(e.target.value))}
            className="bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)] hover:border-[color:var(--border)]"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      )}

      {/* Controles de paginação */}
      <div className="flex items-center gap-1">
        {/* Botão anterior */}
        <button
          type="button"
          aria-label="Página anterior"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded transition-colors ${
            currentPage === 1
              ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] cursor-not-allowed'
              : 'bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] hover:border-[color:var(--border)]'
          }`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Números das páginas */}
        {visiblePages.map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span className="px-3 py-2 text-[color:var(--text-muted)]">...</span>
            ) : (
              <button
                type="button"
                aria-current={page === currentPage ? 'page' : undefined}
                onClick={() => onPageChange(page)}
                className={`px-3 py-2 rounded text-sm transition-colors border ${
                  page === currentPage
                    ? 'bg-[color:var(--accent)] text-white border-[color:var(--accent)] shadow-sm'
                    : 'bg-[color:var(--surface)] text-[color:var(--text-primary)] border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] hover:border-[color:var(--border)]'
                }`}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}

        {/* Botão próximo */}
        <button
          type="button"
          aria-label="Próxima página"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded transition-colors ${
            currentPage === totalPages
              ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] cursor-not-allowed'
              : 'bg-[color:var(--surface)] text-[color:var(--text-primary)] border border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] hover:border-[color:var(--border)]'
          }`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;

